import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, Tag, X, ArrowRight } from "lucide-react";
import { api, fmt } from "../lib/api";
import { useLang } from "../i18n/LanguageContext";
import { lp, productAlt } from "../lib/localize";
import { recherchesRecentes, categoriesRecentes, oublierRecherches } from "../lib/recherches";

// Bloc « Reprendre où vous étiez » (demande du propriétaire, 2026-10-16) : à
// l'arrivée sur le site, propose les recherches et catégories récentes du
// visiteur (lues dans SES témoins — rien n'est pisté côté serveur) et, dans la
// boutique, une rangée de produits suggérés d'après sa dernière recherche.
//   lieu="accueil"  : bande compacte sous le héros (puces → la boutique)
//   lieu="boutique" : puces cliquables + rangée de suggestions
//
// « Nos suggestions du moment » (accueil) — demande du propriétaire
// (2026-10-21) : la sélection change TOUTE SEULE aux 15 minutes. Le mélange est
// SEMÉ par la tranche de quart d'heure en cours : tous les visiteurs voient la
// même sélection pendant la tranche, et une minuterie bascule la grille au
// quart d'heure suivant SANS rechargement de la page.
const TRANCHE_MS = 15 * 60 * 1000;
const tranche15 = () => Math.floor(Date.now() / TRANCHE_MS);
function rngSeme(graine) {
  // mulberry32 — petit générateur déterministe (même graine → même mélange)
  let a = (graine >>> 0) || 1;
  return () => {
    a |= 0; a = (a + 0x6D2B79F5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export default function SuggestionsRecherches({ lieu = "boutique", onRecherche, onCategorie }) {
  const { lang, t } = useLang();
  const [recherches, setRecherches] = useState([]);
  const [categories, setCategories] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [bassin, setBassin] = useState([]);
  const [tranche, setTranche] = useState(tranche15);

  useEffect(() => {
    const r = recherchesRecentes();
    const c = categoriesRecentes();
    setRecherches(r);
    setCategories(c);
    // À l'accueil la grille s'affiche MÊME SANS historique (suggestions générales)
    // ; dans la boutique, le bloc n'apparaît qu'avec un historique (recherche
    // ou catégorie déjà visitée)
    if (lieu === "boutique" && !r.length && !c.length) return;
    // On pêche un BASSIN plus large, puis on en tire 9 (ou 8 en boutique)
    // produits — à l'accueil, le tirage est refait à chaque tranche de 15 min
    const pêches = [];
    if (r[0]) pêches.push(api.get("/products/browse", { params: { q: r[0], limit: 24 } }));
    // Recherche LONGUE venue du web (ex. Google/Bing : « trophée soccer
    // personnalisé » — demande du propriétaire, 2026-10-27) : la phrase entière
    // peut ne rien donner ; on pêche AUSSI mot par mot (≥ 4 lettres) pour que
    // les produits associés au SUJET (soccer → produits de soccer) paraissent
    if (r[0]) {
      for (const mot of r[0].split(/\s+/).filter((m) => m.length >= 4).slice(0, 3)) {
        pêches.push(api.get("/products/browse", { params: { q: mot, limit: 10 } }));
      }
    }
    if (r[1]) pêches.push(api.get("/products/browse", { params: { q: r[1], limit: 12 } }));
    for (const cat of c.slice(0, 2)) {
      pêches.push(api.get("/products/browse", { params: { shop_category: cat, limit: 12 } }));
    }
    // Compléments génériques : produits en vedette (photographiés) puis pêche
    // directe par grande famille — la grille reste pleine même si les recherches
    // récentes offrent peu de fiches avec photo
    pêches.push(api.get("/products/home-featured", { params: { limit: 12 } }));
    for (const fam of ["Trophées", "Médailles", "Plaques"]) {
      pêches.push(api.get("/products/browse", { params: { shop_category: fam, limit: 10 } }));
    }
    // RÈGLE du propriétaire (2026-10-16) : les suggestions 3×3 ne montrent QUE des
    // trophées, médailles ou plaques — JAMAIS de produits dérivés (rubans,
    // chaînes, boîtes, écrins, cordons, accessoires…)
    const CAT_OK = ["trophées", "médailles", "plaques"];
    const NOM_EXCLU = /ruban|ribbon|cha[iî]ne|\bchain\b|bo[iî]te|écrin|ecrin|cordon|lanyard|\bbox(es)?\b|\bcase\b|trousse|\bkit\b|medalbox/i;
    const admissible = (p) =>
      CAT_OK.includes(String(p.shop_category || "").toLowerCase())
      && !NOM_EXCLU.test(`${p.name || ""} ${p.name_en || ""} ${p.category || ""}`);
    Promise.all(pêches.map((p) => p.catch(() => null))).then((res) => {
      const trouve = [];
      const vus = new Set();
      for (const x of res) {
        const liste = Array.isArray(x?.data) ? x.data : (x?.data?.products || []);
        for (const p of liste) {
          // Suggestions visuelles : fiches AVEC photo, trophées/médailles/plaques
          if (p.image && admissible(p) && !vus.has(p.id)) { vus.add(p.id); trouve.push(p); }
        }
      }
      setBassin(trouve);
    });
  }, [lieu]);

  // Tirage des suggestions : à l'ACCUEIL, mélange semé par la tranche de 15 min
  // (rotation automatique, identique pour tous pendant la tranche) ; dans la
  // boutique, mélange libre à chaque chargement (comme avant)
  useEffect(() => {
    if (!bassin.length) return;
    const nVoulu = lieu === "accueil" ? 9 : 8;
    const rnd = lieu === "accueil" ? rngSeme(tranche) : Math.random;
    const copie = [...bassin];
    for (let i = copie.length - 1; i > 0; i--) {
      const j = Math.floor(rnd() * (i + 1));
      [copie[i], copie[j]] = [copie[j], copie[i]];
    }
    setSuggestions(copie.slice(0, nVoulu));
  }, [bassin, tranche, lieu]);

  // Minuterie de rotation (accueil seulement) : dès que le quart d'heure
  // bascule, la grille se renouvelle toute seule — aucun rechargement requis
  useEffect(() => {
    if (lieu !== "accueil") return;
    const minuteur = setInterval(() => {
      const g = tranche15();
      setTranche((t0) => (t0 === g ? t0 : g));
    }, 30000);
    return () => clearInterval(minuteur);
  }, [lieu]);

  const hasHistory = recherches.length > 0 || categories.length > 0;
  if (lieu === "boutique" && !hasHistory) return null;

  const effacer = () => {
    oublierRecherches();
    setRecherches([]);
    setCategories([]);
  };

  const puce = "inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs border border-gold/30 text-gold/90 hover:bg-gold hover:text-ink transition-colors duration-300";

  return (
    <div data-testid={`reco-recherches-${lieu}`}
      className={lieu === "accueil"
        ? ""  // la section hôte (accueil) fournit cadre et espacement
        : "mb-8 bg-surface/60 border border-gold/15 rounded-2xl px-5 py-4"}>
      <div>
        {hasHistory && (
        <div className="flex items-center gap-3 flex-wrap">
          <span className="text-xs uppercase tracking-[0.2em] text-gold/80 font-semibold">
            {t("reco.title")}
          </span>
          {recherches.map((r) =>
            lieu === "accueil" ? (
              <Link key={r} to={`/boutique?q=${encodeURIComponent(r)}`}
                data-testid={`reco-search-${r}`} className={puce}>
                <Search className="w-3 h-3" /> {r}
              </Link>
            ) : (
              <button key={r} type="button" onClick={() => onRecherche?.(r)}
                data-testid={`reco-search-${r}`} className={puce}>
                <Search className="w-3 h-3" /> {r}
              </button>
            ))}
          {categories.map((c) =>
            lieu === "accueil" ? (
              <Link key={c} to={`/boutique?cat=${encodeURIComponent(c)}`}
                data-testid={`reco-cat-${c}`} className={puce}>
                <Tag className="w-3 h-3" /> {c}
              </Link>
            ) : (
              <button key={c} type="button" onClick={() => onCategorie?.(c)}
                data-testid={`reco-cat-${c}`} className={puce}>
                <Tag className="w-3 h-3" /> {c}
              </button>
            ))}
          <button type="button" onClick={effacer} data-testid="reco-clear"
            className="inline-flex items-center gap-1 text-[11px] text-ivory/45 hover:text-red-300 underline underline-offset-2 transition-colors ml-auto">
            <X className="w-3 h-3" /> {t("reco.clear")}
          </button>
        </div>
        )}

        {lieu === "accueil" && suggestions.length > 0 && (
          <div className="mt-4 pb-2">
            <div className="flex items-baseline justify-between gap-3 mb-3">
              <p className="text-[11px] uppercase tracking-widest text-ivory/50" data-testid="reco-grid-title">
                {hasHistory ? t("reco.suggestions") : t("reco.suggestions_generic")}
              </p>
              <Link to="/boutique" data-testid="reco-see-shop"
                className="inline-flex items-center gap-1.5 text-xs text-gold hover:text-champagne transition-colors">
                {t("reco.see")} <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
            {/* 9 suggestions 3×3 d'après les témoins du visiteur (demande du
                propriétaire, 2026-10-16) — clic = fiche produit ; la sélection
                change TOUTE SEULE aux 15 minutes (demande du 2026-10-21) */}
            <div className="grid grid-cols-3 gap-3 md:gap-5 max-w-3xl" data-testid="reco-grid">
              {suggestions.map((p) => (
                <Link key={p.id} to={`/produit/${p.id}`} data-testid={`reco-product-${p.id}`}
                  className="group block">
                  <div className="aspect-square rounded-xl overflow-hidden bg-ink border border-ivory/10 group-hover:border-gold/60 transition-colors duration-300">
                    {p.image && (
                      <img src={p.image} alt={productAlt(p, lang)} loading="eager"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    )}
                  </div>
                  <p className="mt-1.5 text-[11px] text-ivory/75 leading-tight line-clamp-2 group-hover:text-gold transition-colors">
                    {lp(p, lang).name}
                  </p>
                  <p className="text-[11px] text-champagne">{fmt(p.base_price)}</p>
                </Link>
              ))}
            </div>
          </div>
        )}

        {lieu === "boutique" && suggestions.length > 0 && (
          <div className="mt-4">
            <p className="text-[11px] uppercase tracking-widest text-ivory/50 mb-2.5">
              {t("reco.suggestions")}
            </p>
            <div className="flex gap-3 overflow-x-auto pb-1" data-testid="reco-suggestions">
              {suggestions.map((p) => (
                <Link key={p.id} to={`/produit/${p.id}`} data-testid={`reco-product-${p.id}`}
                  className="group shrink-0 w-32">
                  <div className="w-32 h-32 rounded-xl overflow-hidden bg-ink border border-ivory/10 group-hover:border-gold/50 transition-colors">
                    {p.image && (
                      <img src={p.image} alt={productAlt(p, lang)} loading="lazy"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    )}
                  </div>
                  <p className="mt-1.5 text-[11px] text-ivory/75 leading-tight line-clamp-2 group-hover:text-gold transition-colors">
                    {lp(p, lang).name}
                  </p>
                  <p className="text-[11px] text-champagne">{fmt(p.base_price)}</p>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
